import { User, Settings, Shield, Lock, Bell, HelpCircle, LogOut, ChevronRight, Camera, Edit3 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';

const SETTINGS_LINKS = [
  { label: 'Personal Information', icon: User, desc: 'Manage your name, email, and bio', to: '/app/settings' },
  { label: 'Security & Password', icon: Lock, desc: 'Update password and 2FA', to: '/app/settings/security' },
  { label: 'Privacy Settings', icon: Shield, desc: 'Control who sees your activity', to: '/app/settings/location' },
  { label: 'Notification Preferences', icon: Bell, desc: 'Choose what you want to hear about', to: '/app/settings/notifications' },
  { label: 'Help & Support', icon: HelpCircle, desc: 'Get help or contact support', to: '/app/settings/support' },
];

export default function Profile() {
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth/login', { replace: true });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <header className="relative">
        <div className="h-48 bg-neutral-900 rounded-[2.5rem] overflow-hidden relative">
          <img 
            src="https://picsum.photos/seed/cover/1200/400" 
            alt="Cover" 
            className="w-full h-full object-cover opacity-50"
            referrerPolicy="no-referrer"
          />
          <button className="absolute bottom-4 right-6 p-2 bg-white/20 backdrop-blur-md rounded-xl text-white hover:bg-white/30 transition-all">
            <Camera className="w-5 h-5" />
          </button>
        </div>
        <div className="px-8 -mt-16 flex flex-col md:flex-row md:items-end justify-between gap-6 relative z-10">
          <div className="flex flex-col md:flex-row md:items-end gap-6">
            <div className="w-32 h-32 rounded-[2.5rem] border-4 border-white overflow-hidden bg-neutral-100 dark:bg-neutral-800 shadow-xl relative group">
              <img 
                src="https://picsum.photos/seed/user/400/400" 
                alt="Profile" 
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                <Camera className="w-8 h-8 text-white" />
              </div>
            </div>
            <div className="pb-2">
              <h1 className="text-3xl font-bold tracking-tight">Alex Johnson</h1>
              <p className="text-neutral-500 dark:text-neutral-400 font-medium">@alex_johnson • Product Designer</p>
            </div>
          </div>
          <div className="flex items-center gap-3 pb-2">
            <button className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl text-sm font-bold hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-all shadow-sm dark:shadow-none">
              <Edit3 className="w-4 h-4" />
              Edit Profile
            </button>
            <Link to="/app/settings" title="Settings" className="p-3 bg-neutral-900 text-white rounded-2xl hover:bg-neutral-800 transition-all shadow-lg shadow-neutral-900/20">
              <Settings className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Info Sidebar */}
        <div className="space-y-6">
          <div className="bg-white dark:bg-neutral-900 p-8 rounded-[2.5rem] border border-neutral-200 dark:border-neutral-800 shadow-sm dark:shadow-none space-y-6">
            <h3 className="font-bold text-lg">About</h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed">
              Passionate product designer focused on creating unified experiences. Building the future of social commerce at Messego.
            </p>
            <div className="space-y-4 pt-4 border-t border-neutral-50">
              <div className="flex items-center gap-3 text-sm text-neutral-500 dark:text-neutral-400">
                <Shield className="w-4 h-4" />
                <span>Verified Account</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-neutral-500 dark:text-neutral-400">
                <Lock className="w-4 h-4" />
                <span>Private Profile</span>
              </div>
            </div>
          </div>

          <div className="bg-neutral-900 rounded-[2.5rem] p-8 text-white">
            <h3 className="font-bold mb-4">Messego Pro</h3>
            <p className="text-neutral-400 dark:text-neutral-500 text-xs mb-6">You are currently on the free plan. Upgrade to unlock all features.</p>
            <button className="w-full bg-white text-neutral-900 py-3 rounded-2xl font-bold hover:bg-neutral-100 transition-all">
              Upgrade Now
            </button>
          </div>
        </div>

        {/* Settings List */}
        <div className="lg:col-span-2 space-y-6">
          <h2 className="text-xl font-bold px-2">Account Settings</h2>
          <div className="bg-white dark:bg-neutral-900 rounded-[2.5rem] border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm dark:shadow-none">
            {SETTINGS_LINKS.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="w-full p-6 flex items-center justify-between hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors border-b border-neutral-50 last:border-0 group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-neutral-50 dark:bg-neutral-950 flex items-center justify-center text-neutral-600 dark:text-neutral-300 group-hover:bg-neutral-900 group-hover:text-white transition-all">
                    <item.icon className="w-6 h-6" />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-sm">{item.label}</p>
                    <p className="text-[10px] text-neutral-500 dark:text-neutral-400 font-medium">{item.desc}</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-neutral-300 group-hover:text-neutral-900 dark:group-hover:text-neutral-50 transition-colors" />
              </Link>
            ))}
          </div>

          <button onClick={handleSignOut} className="w-full p-6 flex items-center gap-4 text-rose-500 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors rounded-[2.5rem] border border-transparent hover:border-rose-100">
            <div className="w-12 h-12 rounded-2xl bg-rose-50 dark:bg-rose-950/40 flex items-center justify-center">
              <LogOut className="w-6 h-6" />
            </div>
            <span className="font-bold">Sign Out</span>
          </button>
        </div>
      </div>
    </div>
  );
}
